import { useMessageFormatter } from '@dsx-react/i18n';
import { Text } from '@dsx-react/text';
import { classNames } from '@dsx-react/util';
import { useState, useEffect, useMemo, forwardRef, useCallback } from 'react';
import { jsx, jsxs } from 'react/jsx-runtime';

var enPT = {
	"image-not-available": "This image be lost t' the briny depths."
};

var enUS = {
	"image-not-available": "This image is not available."
};

const messages = {
  'en-US': enUS,
  'en-PT': enPT
};
var messages$1 = messages;

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;
  if (!css || typeof document === 'undefined') {
    return;
  }
  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';
  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }
  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Image-module_image__fq4Pp {\n  display: block;\n}\n\n.Image-module_contain__Jiz8l {\n  object-fit: contain;\n}\n\n.Image-module_cover__oGJLm {\n  object-fit: cover;\n}\n\n.Image-module_scale-down__1Mhge {\n  object-fit: scale-down;\n}\n\n.Image-module_none__d55im {\n  object-fit: none;\n}\n\n.Image-module_fill__tQDu5 {\n  object-fit: fill;\n}\n\n.Image-module_failure__Iolj3 {\n  width: 100%;\n  height: 100%;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  padding: var(--size-300);\n  text-align: center;\n}";
var styles = {"image":"Image-module_image__fq4Pp","contain":"Image-module_contain__Jiz8l","cover":"Image-module_cover__oGJLm","scale-down":"Image-module_scale-down__1Mhge","none":"Image-module_none__d55im","fill":"Image-module_fill__tQDu5","failure":"Image-module_failure__Iolj3"};
styleInject(css_248z);

function useImageLoadingState(props) {
  const {
    src,
    srcSet,
    onStateChange
  } = props;
  const [state, setState] = useState('initial');
  useEffect(() => {
    setState('loading');
    onStateChange && onStateChange('loading');
  }, [src, srcSet, onStateChange]);
  const imgProps = useMemo(() => ({
    src,
    srcSet,
    onLoad() {
      setState('success');
      onStateChange && onStateChange('success');
    },
    onError() {
      setState('failure');
      onStateChange && onStateChange('failure');
    }
  }), [src, srcSet, onStateChange]);
  return [state, imgProps];
}

function asElementProps(source) {
  var _source$sizes;
  const media = source.media;
  const type = source.type;
  const sizes = (_source$sizes = source.sizes) === null || _source$sizes === void 0 ? void 0 : _source$sizes.map(x => `${x.media || ''} ${x.width}`.trim()).join(', ');
  const srcSet = source.srcSet.map(x => `${x.src} ${x.size || ''}`.trim()).join(', ');
  return {
    media,
    type,
    sizes,
    srcSet
  };
}

/**
 * An image to display.
 */
const Image = /*#__PURE__*/forwardRef(function Image(props, ref) {
  const {
    src,
    alt,
    objectFit = 'contain',
    className: callerClassName,
    tag,
    sources = [],
    imageLoaded,
    imageFailed,
    onStateChange,
    ...rest
  } = props;
  const {
    formatMessage
  } = useMessageFormatter(messages$1);
  const handleStateChange = useCallback(state => {
    if (onStateChange) {
      onStateChange(state);
    }
    if (state === 'success' && imageLoaded) {
      imageLoaded();
    }
    if (state === 'failure' && imageFailed) {
      imageFailed();
    }
  }, [onStateChange, imageLoaded, imageFailed]);
  const [state, imgProps] = useImageLoadingState({
    srcSet: src,
    onStateChange: handleStateChange
  });
  const sourceEls = sources.map((x, i) => /*#__PURE__*/jsx("source", {
    ...asElementProps(x)
  }, i));
  const className = classNames('dsx-component', styles['image'], state === 'success' && styles[objectFit], state === 'failure' && styles['failure'], callerClassName);
  const pictureEl = /*#__PURE__*/jsxs("picture", {
    children: [sourceEls, /*#__PURE__*/jsx("img", {
      ref: ref,
      alt: alt,
      className: className,
      ...imgProps,
      ...rest
    })]
  });
  switch (state) {
    case 'initial':
    case 'loading':
      {
        // With lazy loading, the image has to be visible to begin the onLoad process.
        const loadingStyle = props.loading === 'lazy' ? {} : {
          display: 'none'
        };
        return /*#__PURE__*/jsx("div", {
          className: className,
          "data-dsx--image": tag,
          children: /*#__PURE__*/jsx("div", {
            style: loadingStyle,
            children: pictureEl
          })
        });
      }
    case 'success':
      return pictureEl;
    case 'failure':
      return /*#__PURE__*/jsx("div", {
        className: className,
        "data-dsx--image": tag,
        children: /*#__PURE__*/jsx(Text, {
          variant: "helper",
          children: formatMessage('image-not-available')
        })
      });
  }
});

export { Image, useImageLoadingState };
